import { create } from "zustand";

/** Coordinates expand/collapse-all of line-item custom field sections across rows. */
interface LineCustomFieldsExpandState {
  /** Bumped each time an expand-all/collapse-all command is issued. */
  version: number;
  /** Desired open state broadcast by the latest command. */
  expanded: boolean;
  /** Ids of line rows whose custom fields are currently open. */
  openIds: Set<string>;
  /** Broadcasts a command to expand or collapse every line's custom fields. */
  setAll: (expanded: boolean) => void;
  /** Registers a row's current open state so the toggle can reflect reality. */
  setOpen: (id: string, open: boolean) => void;
}

/**
 * Store that broadcasts expand/collapse-all commands to per-line custom field
 * collapsibles and tracks which rows are actually open. Rows sync to `expanded`
 * whenever `version` changes, while still managing their own state for
 * individual toggles between commands and reporting it via `setOpen`.
 */
export const useLineCustomFieldsExpandStore =
  create<LineCustomFieldsExpandState>((set) => ({
    version: 0,
    expanded: false,
    openIds: new Set(),
    setAll: (expanded) =>
      set((state) => ({ expanded, version: state.version + 1 })),
    setOpen: (id, open) =>
      set((state) => {
        if (state.openIds.has(id) === open) {
          return state;
        }
        const openIds = new Set(state.openIds);
        if (open) {
          openIds.add(id);
        } else {
          openIds.delete(id);
        }
        return { openIds };
      }),
  }));
